import * as RadixCheckbox from "@radix-ui/react-checkbox";
import * as RadixRadio from "@radix-ui/react-radio-group";
import * as RadixSwitch from "@radix-ui/react-switch";
import { Check, Minus } from "lucide-react";
import { useId } from "react";
import { Icon } from "../icons/Icon";
import { cx, FOCUS_RING } from "../styles";

/**
 * Checkbox, radio group and switch — Radix behaviour under the Khazzin skin.
 *
 * Radix is here for the keyboard and the accessibility tree, which is the half
 * that is tedious to get right and invisible when it is wrong: a radio group
 * that moves with arrow keys and not with Tab, a switch that answers to Space,
 * a checkbox whose indeterminate state is announced as "mixed" rather than as
 * nothing. What this file adds is the paint.
 *
 * **Checkbox and switch are not interchangeable.** A checkbox is part of a form
 * that will be submitted; a switch takes effect the moment it moves. Using one
 * where the other belongs means somebody either presses Save and nothing was
 * pending, or changes a setting for the whole organisation while looking for
 * the Cancel button.
 */

const CONTROL_BASE = "shrink-0 cursor-pointer disabled:cursor-not-allowed";

/**
 * The clickable wrapper that makes the label part of the target.
 *
 * A 16px checkbox is a 16px target, which is under half the 48px floor. Putting
 * the control and its text inside one label gives a thumb the whole row, and
 * costs nothing on a desktop.
 */
function Row({
	htmlFor,
	children,
	className,
}: {
	htmlFor: string;
	children: React.ReactNode;
	className?: string | undefined;
}) {
	return (
		<label
			htmlFor={htmlFor}
			className={cx(
				"inline-flex cursor-pointer items-center gap-2 text-[13px] text-fg",
				"max-sm:min-h-12",
				className,
			)}
		>
			{children}
		</label>
	);
}

export interface CheckboxProps {
	checked?: boolean | "indeterminate";
	defaultChecked?: boolean;
	onCheckedChange?: (checked: boolean | "indeterminate") => void;
	disabled?: boolean;
	/** The text beside the box. Omit for a selection cell in a table, which has a header instead. */
	label?: React.ReactNode;
	/** Required when there is no visible label, or the control is announced as nothing. */
	"aria-label"?: string;
	id?: string;
	name?: string;
	value?: string;
	className?: string;
	/**
	 * The raw click, for a caller that needs the modifier keys.
	 *
	 * `onCheckedChange` reports a boolean, which is the right shape for almost
	 * everything and loses the one thing a table's row selection needs: whether
	 * Shift was held. This runs before it.
	 */
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

export function Checkbox({ label, className, id, ...rest }: CheckboxProps) {
	const generated = useId();
	const controlId = id ?? generated;

	const control = (
		<RadixCheckbox.Root
			id={controlId}
			className={cx(
				CONTROL_BASE,
				"grid size-4 place-items-center rounded-[4px] border-[1.5px] border-border-strong bg-bg-surface",
				"data-[state=checked]:border-brand data-[state=checked]:bg-brand",
				"data-[state=indeterminate]:border-brand data-[state=indeterminate]:bg-brand",
				"disabled:bg-bg-subtle disabled:border-border",
				FOCUS_RING,
			)}
			{...rest}
		>
			<RadixCheckbox.Indicator className="text-fg-on-brand">
				{rest.checked === "indeterminate" ? (
					// A dash, not a tick. "Some of these are selected" is a different
					// statement from "all of these are", and a half-tick is neither.
					<Icon as={Minus} size={12} />
				) : (
					<Icon as={Check} size={12} />
				)}
			</RadixCheckbox.Indicator>
		</RadixCheckbox.Root>
	);

	if (label === undefined) {
		return <span className={className}>{control}</span>;
	}
	return (
		<Row htmlFor={controlId} className={className}>
			{control}
			{label}
		</Row>
	);
}

export interface RadioOption {
	value: string;
	label: React.ReactNode;
	disabled?: boolean | undefined;
}

export interface RadioGroupProps {
	options: RadioOption[];
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	name?: string;
	disabled?: boolean;
	/** Names the whole group. A group of radios with no name is a list of loose options. */
	"aria-label"?: string;
	"aria-labelledby"?: string;
	/** Lay the options out in a row instead of a column. */
	horizontal?: boolean;
	className?: string;
}

export function RadioGroup({ options, horizontal = false, className, ...rest }: RadioGroupProps) {
	const group = useId();

	return (
		<RadixRadio.Root
			className={cx("flex gap-3", horizontal ? "flex-row flex-wrap" : "flex-col", className)}
			{...rest}
		>
			{options.map((option) => {
				const id = `${group}-${option.value}`;
				return (
					<Row key={option.value} htmlFor={id}>
						<RadixRadio.Item
							id={id}
							value={option.value}
							disabled={option.disabled}
							className={cx(
								CONTROL_BASE,
								"size-4 rounded-full border-[1.5px] border-border-strong bg-bg-surface",
								// The checked state is a 5px brand ring rather than a dot
								// inside a circle: one element, and it keeps its shape at
								// any zoom level.
								"data-[state=checked]:border-[5px] data-[state=checked]:border-brand",
								"disabled:bg-bg-subtle disabled:border-border",
								FOCUS_RING,
							)}
						/>
						{option.label}
					</Row>
				);
			})}
		</RadixRadio.Root>
	);
}

export interface SwitchProps {
	checked?: boolean;
	defaultChecked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
	disabled?: boolean;
	label?: React.ReactNode;
	"aria-label"?: string;
	id?: string;
	name?: string;
	className?: string;
}

export function Switch({ label, className, id, ...rest }: SwitchProps) {
	const generated = useId();
	const controlId = id ?? generated;

	const control = (
		<RadixSwitch.Root
			id={controlId}
			className={cx(
				CONTROL_BASE,
				"relative h-[22px] w-[38px] rounded-full border border-border-strong bg-neutral-bg p-0.5",
				// `--accent` is the on state, and it is the one place in the system
				// where the yellow is a fill rather than a marker. It never carries
				// text, which is the rule that keeps it usable.
				"data-[state=checked]:border-accent data-[state=checked]:bg-accent",
				"disabled:bg-bg-subtle",
				FOCUS_RING,
			)}
			{...rest}
		>
			<RadixSwitch.Thumb
				className={cx(
					"block size-[18px] rounded-full bg-bg-surface shadow-xs transition-transform",
					// Translated rather than justified, so the thumb slides instead of
					// jumping — and `rtl:-translate-x-*` because "on" is the end of the
					// track, which is the left in Arabic.
					"translate-x-0 data-[state=checked]:translate-x-4 rtl:data-[state=checked]:-translate-x-4",
					"motion-reduce:transition-none",
				)}
			/>
		</RadixSwitch.Root>
	);

	if (label === undefined) {
		return <span className={className}>{control}</span>;
	}
	return (
		<Row htmlFor={controlId} className={className}>
			{control}
			{label}
		</Row>
	);
}
